import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

/* ══════════════════════════════════════════════════════════════
   Brand + numbers context.
   The hero form writes into this. Every section downstream reads
   from it, so the page speaks in their brand and their numbers.
   ══════════════════════════════════════════════════════════════ */

const DEFAULT_AGENT = "Your Agent";

/* assumed contribution margin left after COGS, shipping and fees. Everything
   else is worked out from numbers the founder actually typed. */
const CONTRIBUTION = 0.6;
const REPEAT_30 = 0.35;   // share of first-order margin repeated every 30 days

export function money(n, opts = {}) {
  if (n === null || n === undefined || Number.isNaN(n)) return "n/a";
  const v = Math.round(n);
  if (opts.short && Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}m`;
  if (opts.short && Math.abs(v) >= 10000) return `$${Math.round(v / 1000)}k`;
  return `$${v.toLocaleString("en-US")}`;
}

export function deriveNumbers(revenue, cpa, aov) {
  const R = Number(revenue) || 0;
  const C = Number(cpa) || 0;
  const A = Number(aov) || 0;
  if (R <= 0 || C <= 0 || A <= 0) return null;

  const orders = R / A;
  const spend = orders * C;
  const mer = R / spend; // equals A / C

  const driftedCpa = C * 1.13;
  const driftCost = orders * (driftedCpa - C);
  const wastedSearch = spend * 0.061;
  const repeatAtRisk = R * 0.038;
  const pageUpside = R * 0.11;
  const quietWinnerSpend = spend * 0.02;
  const quietWinnerUpside = quietWinnerSpend * 6 * 0.55;

  const firstOrderMargin = A * CONTRIBUTION;
  const repeat30 = firstOrderMargin * REPEAT_30;
  const paybackDays =
    firstOrderMargin >= C ? 0 : Math.round((30 * (C - firstOrderMargin)) / Math.max(repeat30, 1));
  const targetCpa60 = firstOrderMargin * (1 + REPEAT_30 * 2);
  const targetCpa90 = firstOrderMargin * (1 + REPEAT_30 * 3);

  const targetAov = A * 1.18;
  const targetMer = targetAov / C;
  const aovUpside = orders * (targetAov - A);
  const merUpside = spend * (targetMer - mer);

  return {
    revenue: R,
    cpa: C,
    spend,
    orders,
    aov: A,
    mer,
    driftedCpa,
    driftCost,
    wastedSearch,
    repeatAtRisk,
    pageUpside,
    quietWinnerSpend,
    quietWinnerUpside,
    firstOrderMargin,
    paybackDays,
    targetCpa60,
    targetCpa90,
    targetMer,
    targetAov,
    aovUpside,
    merUpside,
  };
}

const Ctx = createContext(null);

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState("");
  const [brandUrl, setBrandUrl] = useState("");
  const [revenue, setRevenue] = useState(null);
  const [cpa, setCpa] = useState(null);
  const [aov, setAov] = useState(null);
  const [offerMode, setOfferMode] = useState(null); // "ltv" | "aov"
  const [skipped, setSkipped] = useState(false);

  const applyNumbers = useCallback(({ brand: b, brandUrl: u, revenue: r, cpa: c, aov: a }) => {
    setBrand((b || "").trim());
    setBrandUrl((u || "").trim());
    setRevenue(Number(r) || null);
    setCpa(Number(c) || null);
    setAov(Number(a) || null);
  }, []);

  const reset = useCallback(() => {
    setBrand("");
    setBrandUrl("");
    setRevenue(null);
    setCpa(null);
    setAov(null);
    setSkipped(false);
  }, []);

  const value = useMemo(() => {
    const n = deriveNumbers(revenue, cpa, aov);
    return {
      brand,
      brandUrl,
      revenue,
      cpa,
      aov,
      offerMode,
      setOfferMode,
      skipped,
      setSkipped,
      applyNumbers,
      reset,
      hasNumbers: !!n,
      n,
      agentName: brand ? `${brand}'s Agent` : DEFAULT_AGENT,
      possessive: brand || "your brand",
      kpiLabel: offerMode === "aov" ? "MER and order value" : offerMode === "ltv" ? "payback period" : null,
      kpiShort: offerMode === "aov" ? "MER" : offerMode === "ltv" ? "payback" : null,
    };
  }, [brand, brandUrl, revenue, cpa, aov, offerMode, skipped, applyNumbers, reset]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBrand() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useBrand must be used inside BrandProvider");
  return v;
}
